import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';

/**
 * El aviso de versión nueva, en las dos formas que caben según dónde estemos.
 *
 * - `UpdatePrompt`: el globo flotante de siempre. **Sólo por debajo de `lg`.**
 * - `UpdateRow`: una fila más del pie de la lateral, sin aspa.
 *
 * Es el mismo reparto que ya tenía `InstallPrompt`, y por el mismo motivo: una
 * cosa que flota por encima del contenido cuando existe un sitio permanente
 * donde ponerla es ruido. Además el globo se centra respecto a la **ventana**,
 * así que con los 240px de lateral salía descuadrado.
 *
 * ## Por qué un contexto y no dos veces el mismo componente
 *
 * `InstallPrompt` resuelve esto con una prop `variant`, y aquí no vale.
 * `useInstallPWA` sólo escucha eventos, así que llamarlo dos veces es gratis;
 * `useRegisterSW` **registra el service worker y monta un `setInterval` de
 * treinta minutos**. Dos instancias serían dos comprobaciones periódicas y dos
 * registros, y además cada una con su propio `needRefresh`: descartar el globo
 * no apagaría la fila.
 *
 * El proveedor lo llama una vez y las dos vistas leen lo mismo. Va aquí y no en
 * un fichero aparte porque lo único que hace es sostener a los dos de abajo.
 */

interface EstadoDeActualizacion {
  hayVersionNueva: boolean;
  recargar: () => void;
  descartar: () => void;
}

/**
 * Sin proveedor no hay aviso, y eso es lo correcto: es lo que pasa en un test
 * que monta la lateral suelta, y una fila que anuncia una versión que nadie ha
 * comprobado sería peor que no tenerla.
 */
const Contexto = createContext<EstadoDeActualizacion>({
  hayVersionNueva: false,
  recargar: () => {},
  descartar: () => {},
});

/**
 * Le dice al worker que espera que tome el mando. Devuelve si había alguno:
 * quien llama necesita saberlo para no quedarse esperando una recarga que no
 * va a llegar.
 */
function aplicar(registration: ServiceWorkerRegistration): boolean {
  if (!registration.waiting) return false;
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

export function UpdateProvider({ children }: { children: ReactNode }) {
  const recargando = useRef(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // Aquí ya no se mira `registration.waiting`: de eso se encarga el efecto
      // de abajo, que además sabe **si estamos arrancando**, que es lo que
      // decide entre aplicar y preguntar. Ponerlo también aquí encendía el
      // aviso medio segundo antes de recargar: un globo que parpadea y se va.

      // Chequeo periódico cada 30 min
      const CHECK_INTERVAL = 30 * 60 * 1000;
      setInterval(() => {
        void registration.update();
      }, CHECK_INTERVAL);
    },
    onRegisterError(error) {
      // eslint-disable-next-line no-console
      console.warn('SW register failed', error);
    },
  });

  /**
   * **La recarga es nuestra, y ése era el fallo.**
   *
   * `updateServiceWorker(true)` del plugin **no recarga la página**: su
   * argumento se llama `_reloadPage` y no lo usa —lo único que hace es mandar
   * `skipWaiting`—. Quien recarga es un listener de `controlling` que el plugin
   * engancha **sólo dentro de `showSkipWaitingPrompt`**, o sea únicamente
   * cuando workbox emitió su propio evento `waiting` en esa carga de página.
   *
   * Como aquí el worker en espera se detecta por nuestra cuenta con
   * `getRegistration()`, en las cargas en las que workbox no emitía nada no
   * había listener y la recarga no llegaba. Ése es el «a veces sí y a veces
   * no» del botón — y le pasaba igual a la actualización automática, que
   * llamaba a la misma función.
   *
   * Se sustituye por `controllerchange`, que es del navegador y no del plugin:
   * se manda `SKIP_WAITING` al worker que espera —`sw.ts` ya escucha ese
   * mensaje y al activarse hace `clients.claim()`— y se recarga cuando el
   * control cambia de verdad. Deja de depender de por dónde vino el aviso.
   */
  useEffect(() => {
    // Se mira el **valor** y no `'serviceWorker' in navigator`: la clave puede
    // existir valiendo `undefined`, y entonces la guarda pasaba y la limpieza
    // reventaba al desmontar. Lo destapó el caso «sin service worker en el
    // navegador no revienta», que hasta ese momento pasaba por casualidad.
    const sw = navigator.serviceWorker as ServiceWorkerContainer | undefined;
    if (!sw) return;

    // Sin controlador previo es la primera visita: el worker toma el mando por
    // primera vez y no hay nada viejo que sustituir. Recargar ahí sería
    // recargarle la página a todo el que entra por primera vez.
    const habiaControlador = Boolean(sw.controller);
    const alCambiarControl = () => {
      // `controllerchange` puede llegar más de una vez, y dos `reload()`
      // seguidos dejan la carga a medias.
      if (!habiaControlador || recargando.current) return;
      recargando.current = true;
      window.location.reload();
    };

    sw.addEventListener('controllerchange', alCambiarControl);
    return () => sw.removeEventListener('controllerchange', alCambiarControl);
  }, []);

  /**
   * **Al arrancar se aplica solo; en marcha se pregunta.**
   *
   * Con `registerType: 'prompt'` a secas, quien no pulsara el aviso se quedaba
   * en la versión vieja **para siempre**, y recargar no lo arreglaba: un worker
   * en espera sobrevive a las recargas por diseño, así que la única salida era
   * cerrar todas las pestañas. Con un fallo ya corregido en el código viejo
   * —que es cómo salió esto a la luz, con la puerta del consentimiento
   * preguntando en cada recarga— eso es una trampa cerrada.
   *
   * Y no vale atarlo a iniciar sesión, que era lo primero que se pensó: quien ya
   * tiene sesión guardada no inicia ninguna al abrir la app —`supabase-js` da
   * `INITIAL_SESSION`, no `SIGNED_IN`—, o sea que justo el caso que duele se
   * quedaba fuera. Arrancar cubre ése **y** el de entrar, porque entrar viene
   * casi siempre detrás de abrir.
   *
   * Lo que **no** se hace es `registerType: 'autoUpdate'`, que sería una palabra
   * en vez de todo esto y borraría los dos componentes de abajo. Recarga la
   * página en cuanto el worker activa, sin mirar qué estabas haciendo: en una
   * aplicación de dinero eso es un formulario de gasto a medio escribir tirado a
   * la basura. Por eso el aviso sigue existiendo para lo que llega **en marcha**,
   * que es lo único que puede pillarte escribiendo.
   *
   * No hay bucle de recargas: `updateServiceWorker(true)` recarga sólo después
   * de que el worker nuevo tome el control, y para entonces ya no queda ninguno
   * esperando. Si la activación fallara no habría recarga, que es aburrido pero
   * no es un bucle.
   */
  useEffect(() => {
    const mirar = (alArrancar: boolean) => {
      void navigator.serviceWorker?.getRegistration().then((reg) => {
        if (!reg?.waiting) {
          void reg?.update();
          return;
        }
        if (alArrancar) aplicar(reg);
        else setNeedRefresh(true);
      });
    };

    if (document.visibilityState === 'visible') mirar(true);
    const onChange = () => {
      if (document.visibilityState === 'visible') mirar(false);
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [setNeedRefresh]);

  return (
    <Contexto.Provider
      value={{
        hayVersionNueva: needRefresh,
        recargar: () => {
          // Si no hay worker esperando, el botón no tiene nada que activar.
          // Antes eso era un botón que no hacía nada; ahora recarga igual, que
          // es lo que quien lo pulsa está pidiendo.
          void navigator.serviceWorker?.getRegistration().then((reg) => {
            if (!reg || !aplicar(reg)) window.location.reload();
          });
        },
        descartar: () => setNeedRefresh(false),
      }}
    >
      {children}
    </Contexto.Provider>
  );
}

/**
 * La fila de la lateral.
 *
 * Va en color de marca y no en gris como Buscar o Salir: las otras dos están
 * siempre y ésta aparece de higos a brevas para decir algo que caduca. Un aviso
 * que se pinta igual que el mobiliario no se lee.
 *
 * Sin aspa, como la fila de instalar: descartarla la dejaría escondida hasta el
 * siguiente arranque, y aquí lo que se descarta es enterarse de que el código
 * que estás ejecutando ya no es el que hay. Quien no quiera recargar, no pulsa.
 */
export function UpdateRow() {
  const { t } = useTranslation();
  const { hayVersionNueva, recargar } = useContext(Contexto);

  if (!hayVersionNueva) return null;

  return (
    <div className="shrink-0 border-t border-neutral-200 p-3">
      <button
        type="button"
        onClick={recargar}
        className="bg-brand flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm text-white transition-opacity hover:opacity-90"
      >
        <RefreshCw size={18} className="shrink-0" />
        <span className="flex-1 text-left">{t('app.update_available')}</span>
        <span className="text-xs font-semibold underline">{t('app.update_reload')}</span>
      </button>
    </div>
  );
}

export function UpdatePrompt() {
  const { t } = useTranslation();
  const { hayVersionNueva, recargar, descartar } = useContext(Contexto);

  if (!hayVersionNueva) return null;

  return (
    <div
      className="fixed inset-x-0 z-50 mx-auto flex max-w-md items-center gap-3 rounded-lg bg-brand px-4 py-3 text-white shadow-lg lg:hidden"
      style={{
        // Sobre la barra inferior, con su hueco. `--nav-inferior` vale 0 en
        // escritorio, donde no hay barra, y entonces sólo queda la separación.
        bottom: 'calc(env(safe-area-inset-bottom) + var(--nav-inferior) + 17px)',
        marginLeft: '1rem',
        marginRight: '1rem',
      }}
      role="status"
      aria-live="polite"
    >
      <RefreshCw size={16} className="shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-medium">{t('app.update_available')}</div>
        <div className="text-xs text-white/80">{t('app.update_hint')}</div>
      </div>
      <button
        type="button"
        onClick={recargar}
        className="rounded bg-white px-3 py-1 text-xs font-semibold text-brand hover:bg-white/90"
      >
        {t('app.update_reload')}
      </button>
      <button
        type="button"
        onClick={descartar}
        aria-label={t('common.close')}
        className="shrink-0 rounded p-1 text-white/70 hover:bg-white/10 hover:text-white"
      >
        <X size={14} />
      </button>
    </div>
  );
}
